import { useCallback } from 'react';

import { useSelector } from 'react-redux';

import { StreamTypes } from 'store/player/actions.types';
import { RBProgram } from 'store/railsBuilder/reducer.types';
import { selectCachedRecordingByEventId } from 'store/recordings/selectors';
import {
    selectAppSessionId,
    selectVideoSessionId,
} from 'store/session/selectors';

import { VideoItem, VodEditorial } from 'services/data-manager/types';
import {
    PlaybackStopUavParams,
    UavContentTypes,
    UavEvents,
} from 'services/uav/types';
import useGetBaseUavParams from 'services/uav/useGetBaseUavParams';
import {
    MOBILE_PLAYBACK_CONTENT_SOURCE,
    getPlayBackType,
} from 'services/uav/utils';

type GetPlaybackPauseUavParamsProps = Omit<
    PlaybackStopUavParams['metadata'],
    | 'appSessionId'
    | 'sessionId'
    | 'contentType'
    | 'contentSource'
    | 'editorialID'
    | 'playbackSessionId'
    | 'appName'
    | 'appVersion'
    | 'deviceType'
>;

type Props = {
    playbackType: StreamTypes | undefined;
    videoItem: VideoItem | undefined;
};

const useGetPlaybackPauseUavParams = ({ playbackType, videoItem }: Props) => {
    const appSessionId = useSelector(selectAppSessionId);
    const genericVideoSessionId = useSelector(selectVideoSessionId);
    const recordingItem = useSelector(
        selectCachedRecordingByEventId(videoItem?.id),
    );

    const getBaseUavParams = useGetBaseUavParams();

    const getPlaybackPauseUavParams = useCallback(
        (
            params: GetPlaybackPauseUavParamsProps,
        ): PlaybackStopUavParams | undefined => {
            const baseUavParams = getBaseUavParams(UavEvents.PLAYBACK_PAUSE);

            if (
                baseUavParams &&
                appSessionId &&
                videoItem &&
                genericVideoSessionId
            ) {
                const contentType = getPlayBackType(
                    playbackType,
                    videoItem.originalProgram as VodEditorial | RBProgram,
                );
                const recordingId =
                    (contentType === UavContentTypes.NPVR && {
                        recordingId: recordingItem?.id,
                    }) ||
                    undefined;

                return {
                    ...baseUavParams,
                    metadata: {
                        ...params,
                        appSessionId,
                        contentSource: MOBILE_PLAYBACK_CONTENT_SOURCE,
                        contentType,
                        editorialID: videoItem.id,
                        playbackSessionId: genericVideoSessionId,
                        ...recordingId,
                        ...baseUavParams.metadata,
                    },
                };
            }

            return undefined;
        },
        [
            getBaseUavParams,
            appSessionId,
            playbackType,
            videoItem,
            genericVideoSessionId,
            recordingItem,
        ],
    );

    return {
        getPlaybackPauseUavParams,
    };
};

export default useGetPlaybackPauseUavParams;
